import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, X, Plus, Loader2 } from 'lucide-react';
import { getProducts } from '@/lib/api';
import { EcwidLink } from '@/components/EcwidWidgets';
import { Price } from '@/components/ProductCard';
import { track } from '@/lib/seo';

export default function SearchOverlay({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await getProducts({ search: query, per_page: 8 });
        setResults(data);
        track('search', { term: query });
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[85] bg-bone/95 backdrop-blur-xl"
        >
          <div className="mx-auto max-w-3xl px-6 pt-24">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-bronze">Search the menu</p>
              <button data-testid="search-close-button" onClick={onClose} aria-label="Close search" className="p-2 text-sage hover:text-ink">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="relative mt-4 border-b border-ink/20 pb-3">
              <Search className="absolute left-0 top-1/2 h-6 w-6 -translate-y-1/2 text-ash" />
              <input
                ref={inputRef}
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Strain, brand, category…"
                className="w-full bg-transparent pl-10 font-serif text-3xl text-ink placeholder:text-ash/60 focus:outline-none sm:text-4xl"
              />
            </div>

            <div className="mt-6 max-h-[55vh] overflow-y-auto pb-10">
              {loading && (
                <div className="flex items-center gap-2 py-6 text-sm text-sage">
                  <Loader2 className="h-4 w-4 animate-spin" /> Searching…
                </div>
              )}
              {!loading && query && results.length === 0 && (
                <p data-testid="search-no-results" className="py-6 text-sm text-sage">No products found for “{query}”. Try a strain name or category.</p>
              )}
              <ul className="divide-y divide-ink/5">
                {results.map((p) => (
                  <li key={p.id} data-testid={`search-result-${p.slug}`} className="flex items-center gap-4 py-4">
                    <div className="h-16 w-16 shrink-0 overflow-hidden border border-ink/10 bg-surface">
                      {p.image && <img src={p.image} alt={p.name} className="h-full w-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-ink">{p.name}</p>
                      <p className="text-sm text-bronze"><Price product={p} /></p>
                    </div>
                    <EcwidLink
                      to={`/shop#!/~/product/id=${p.id}`}
                      onClick={onClose}
                      data-testid={`search-add-${p.slug}`}
                      className="flex items-center gap-1.5 border border-gold/50 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-bronze transition-all hover:bg-gold hover:text-ink"
                    >
                      <Plus className="h-3.5 w-3.5" /> View
                    </EcwidLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
